import React, {
    createContext,
    useContext,
    useCallback,
    ReactNode,
} from "react";
import Logger from "@/utils/Logger";
import { AppError, ErrorCategory, ErrorSeverity } from "@/utils/errors";

/**
 * Error information shape
 */
export interface ErrorInfo {
    error: Error;
    errorInfo?: React.ErrorInfo;
    timestamp: Date;
}

/**
 * Error boundary context value shape
 */
interface ErrorBoundaryContextValue {
    error: ErrorInfo | null;
    clearError: () => void;
    handleError: (error: Error, errorInfo?: React.ErrorInfo) => void;
}

/**
 * Error boundary context for managing application-wide error state
 */
const ErrorBoundaryContext = createContext<
    ErrorBoundaryContextValue | undefined
>(undefined);

/**
 * Hook to access error boundary context
 * @throws Error if used outside ErrorBoundaryProvider
 */
export const useErrorBoundary = (): ErrorBoundaryContextValue => {
    const context = useContext(ErrorBoundaryContext);
    if (!context) {
        throw new Error(
            "useErrorBoundary must be used within an ErrorBoundaryProvider",
        );
    }
    return context;
};

/**
 * Error boundary provider props
 */
interface ErrorBoundaryProviderProps {
    children: ReactNode;
    onError?: (error: Error, errorInfo?: React.ErrorInfo) => void;
    fallback?: (error: ErrorInfo, clearError: () => void) => ReactNode;
}

/**
 * Error boundary provider component
 * Provides error handling context to child components
 */
export const ErrorBoundaryProvider: React.FC<ErrorBoundaryProviderProps> = ({
    children,
    onError,
    fallback,
}) => {
    const [error, setError] = React.useState<ErrorInfo | null>(null);

    const handleError = useCallback(
        (err: Error, errorInfo?: React.ErrorInfo) => {
            const errorData: ErrorInfo = {
                error: err,
                errorInfo,
                timestamp: new Date(),
            };

            setError(errorData);

            // Log error with appropriate severity
            if (err instanceof AppError) {
                switch (err.severity) {
                    case ErrorSeverity.CRITICAL:
                        Logger.error("Critical error occurred", err, ["ErrorBoundary"]);
                        break;
                    case ErrorSeverity.HIGH:
                        Logger.error("High severity error occurred", err, [
                            "ErrorBoundary",
                        ]);
                        break;
                    case ErrorSeverity.MEDIUM:
                        Logger.warn("Medium severity error occurred", ["ErrorBoundary"]);
                        break;
                    default:
                        Logger.info("Low severity error occurred", ["ErrorBoundary"]);
                }
            } else {
                Logger.error("Unhandled error occurred", err, ["ErrorBoundary"]);
            }

            // Call optional error handler
            onError?.(err, errorInfo);
        },
        [onError],
    );

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value: ErrorBoundaryContextValue = {
        error,
        clearError,
        handleError,
    };

    if (error && fallback) {
        return <>{fallback(error, clearError)}</>;
    }

    return (
        <ErrorBoundaryContext.Provider value={value}>
            {children}
        </ErrorBoundaryContext.Provider>
    );
};
